/**
 * InvoicesPage — read-only list of the user's paid transactions.
 * Data comes from GET /api/payments/invoices (payment_transactions where status="paid").
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Receipt, CreditCard, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate("/login?redirect=/invoices"); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/payments/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        setRows(data.invoices || []);
      } finally { setLoading(false); }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 mb-4" data-testid="invoices-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No payment history yet.</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="text-xs text-slate-500 uppercase tracking-wider border-b">
                  <tr>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Plan</th>
                    <th className="text-left py-2 px-2">Provider</th>
                    <th className="text-right py-2 px-2">Amount</th>
                    <th className="text-left py-2 px-2">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => (
                    <tr key={inv.txn_id} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-2.5 px-2 text-slate-700 dark:text-slate-300">
                        {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2.5 px-2 capitalize">{inv.package_id}</td>
                      <td className="py-2.5 px-2 capitalize flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                        {inv.provider}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono">
                        ${Number(inv.amount).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-2 font-mono text-[11px] text-slate-500 truncate max-w-[160px]">
                        {inv.txn_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
